import React, { useState, useEffect } from "react";
import { validateEmployment } from "../../../utils/validation";
import "../Education/styles/EducationForm.css";

const EmploymentForm = ({
  employment = null,
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    company: "",
    jobTitle: "",
    // separate month/year for UI
    startYear: "",
    startMonth: "",
    endYear: "",
    endMonth: "",
    // ISO dates for backend
    startDate: "",
    endDate: "",
    isOngoing: false,
    responsibilities: "",
    reasonForLeaving: "",
    verifierEmail: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (employment) {
      const startDate = employment.startDate
        ? employment.startDate.split("T")[0]
        : "";
      const endDate = employment.endDate
        ? employment.endDate.split("T")[0]
        : "";
      setFormData({
        company: employment.company || "",
        jobTitle: employment.jobTitle || "",
        startDate,
        endDate,
        startYear: startDate ? startDate.slice(0, 4) : "",
        startMonth: startDate ? startDate.slice(5, 7) : "",
        endYear: endDate ? endDate.slice(0, 4) : "",
        endMonth: endDate ? endDate.slice(5, 7) : "",
        isOngoing: employment.isOngoing || false,
        responsibilities: employment.responsibilities || "",
        reasonForLeaving: employment.reasonForLeaving || "",
        verifierEmail: employment.verifierEmail || "",
      });
    } else {
      setFormData({
        company: "",
        jobTitle: "",
        startYear: "",
        startMonth: "",
        endYear: "",
        endMonth: "",
        startDate: "",
        endDate: "",
        isOngoing: false,
        responsibilities: "",
        reasonForLeaving: "",
        verifierEmail: "",
      });
    }

    setErrors({});
    setTouched({});
  }, [employment, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
      ...(name === "isOngoing" && newValue
        ? { endDate: "", endYear: "", endMonth: "" }
        : {}),
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // helpers for month/year selects, also update ISO dates
  const updateStart = (part, value) => {
    setFormData((prev) => {
      const startYear = part === "year" ? value : prev.startYear;
      const startMonth = part === "month" ? value : prev.startMonth;
      const startDate =
        startYear && startMonth ? `${startYear}-${startMonth}-01` : "";
      return { ...prev, startYear, startMonth, startDate };
    });
    if (errors.startDate) {
      setErrors((prev) => ({ ...prev, startDate: "" }));
    }
  };

  const updateEnd = (part, value) => {
    setFormData((prev) => {
      const endYear = part === "year" ? value : prev.endYear;
      const endMonth = part === "month" ? value : prev.endMonth;
      const endDate =
        endYear && endMonth ? `${endYear}-${endMonth}-01` : "";
      return { ...prev, endYear, endMonth, endDate };
    });
    if (errors.endDate) {
      setErrors((prev) => ({ ...prev, endDate: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldErrors = validateEmployment(formData);
    setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] || "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    const validationErrors = validateEmployment(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const submitData = {
        ...formData,
        startDate: formData.startDate || null,
        endDate: formData.isOngoing ? null : formData.endDate || null,
        responsibilities: formData.responsibilities || null,
        reasonForLeaving: formData.reasonForLeaving || null,
      };

      onSubmit(submitData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="edu-modal-overlay" onClick={!loading ? onClose : undefined}>
      <div className="edu-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edu-modal-header">
          <div>
            <h2 className="edu-modal-title">
              {employment ? "Edit Employment" : "Add Employment"}
            </h2>
            <p className="edu-modal-subtitle">
              Add your work experience details.
            </p>
          </div>

          <button
            type="button"
            className="edu-modal-close"
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <form className="edu-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Employment Details</h3>

            <div className="form-group">
              <label>
                Company <span className="edu-required">*</span>
              </label>
              <input
                className={`edu-input ${
                  touched.company && errors.company ? "error" : ""
                }`}
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., Google"
                disabled={loading}
              />
              {touched.company && errors.company && (
                <div className="edu-error">{errors.company}</div>
              )}
            </div>

            <div className="form-group">
              <label>
                Job title <span className="edu-required">*</span>
              </label>
              <input
                className={`edu-input ${
                  touched.jobTitle && errors.jobTitle ? "error" : ""
                }`}
                type="text"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., Software Engineer"
                disabled={loading}
              />
              {touched.jobTitle && errors.jobTitle && (
                <div className="edu-error">{errors.jobTitle}</div>
              )}
            </div>

            {/* Duration */}
          <div className="form-section">
            <h3>Duration</h3>

            <div className="form-row edu-row">
              <div className="form-group edu-form-group">
                <label>
                  Start Month <span className="required edu-required">*</span>
                </label>
                <select
                  value={formData.startMonth}
                  onChange={(e) => updateStart("month", e.target.value)}
                  className="edu-input select-input"
                >
                  <option value="">Select month</option>
                  {[
                    "01",
                    "02",
                    "03",
                    "04",
                    "05",
                    "06",
                    "07",
                    "08",
                    "09",
                    "10",
                    "11",
                    "12",
                  ].map((m) => (
                    <option key={m} value={m}>
                      {new Date(2000, parseInt(m, 10) - 1, 1).toLocaleString(
                        "en-US",
                        { month: "long" }
                      )}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group edu-form-group">
                <label>
                  Start Year <span className="required edu-required">*</span>
                </label>
                <select
                  value={formData.startYear}
                  onChange={(e) => updateStart("year", e.target.value)}
                  className="edu-input select-input"
                >
                  <option value="">Select year</option>
                  {Array.from({ length: 50 }, (_, i) => 1980 + i).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group checkbox-group edu-checkbox-row">
  <label className="checkbox-label edu-checkbox-label">
    <input
      type="checkbox"
      name="isOngoing"
      checked={formData.isOngoing}
      onChange={handleChange}
      className="edu-checkbox-input"
    />
    <span className="edu-checkbox-custom" />
  <span className="edu-checkbox-text">I currently attend this institution</span>
  </label>
</div>


            {!formData.isOngoing && (
              <div className="form-row edu-row">
                <div className="form-group edu-form-group">
                  <label>
                    End Month <span className="required edu-required">*</span>
                  </label>
                  <select
                    value={formData.endMonth}
                    onChange={(e) => updateEnd("month", e.target.value)}
                    className="edu-input select-input"
                  >
                    <option value="">Select month</option>
                    {[
                      "01",
                      "02",
                      "03",
                      "04",
                      "05",
                      "06",
                      "07",
                      "08",
                      "09",
                      "10",
                      "11",
                      "12",
                    ].map((m) => (
                      <option key={m} value={m}>
                        {new Date(
                          2000,
                          parseInt(m, 10) - 1,
                          1
                        ).toLocaleString("en-US", { month: "long" })}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group edu-form-group">
                  <label>
                    End Year <span className="required edu-required">*</span>
                  </label>
                  <select
                    value={formData.endYear}
                    onChange={(e) => updateEnd("year", e.target.value)}
                    className="edu-input select-input"
                  >
                    <option value="">Select year</option>
                    {Array.from({ length: 50 }, (_, i) => 1980 + i).map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

            <div className="form-group">
              <label>Roles & Responsibilities</label>
              <textarea
                className={`edu-input ${
                  touched.responsibilities && errors.responsibilities ? "error" : ""
                }`}
                name="responsibilities"
                value={formData.responsibilities}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="What did you work on?"
                rows={4}
                disabled={loading}
              />
              {touched.responsibilities && errors.responsibilities && (
                <div className="edu-error">{errors.responsibilities}</div>
              )}
            </div>

            {!formData.isOngoing && (
              <div className="form-group">
                <label>Reason for leaving</label>
                <input
                  className={`edu-input ${
                    touched.reasonForLeaving && errors.reasonForLeaving ? "error" : ""
                  }`}
                  type="text"
                  name="reasonForLeaving"
                  value={formData.reasonForLeaving}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Optional"
                  disabled={loading}
                />
                {touched.reasonForLeaving && errors.reasonForLeaving && (
                  <div className="edu-error">{errors.reasonForLeaving}</div>
                )}
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>Verifier</h3>

            <div className="form-group">
              <label>
                Verifier email <span className="edu-required">*</span>
              </label>
              <input
                className={`edu-input ${
                  touched.verifierEmail && errors.verifierEmail ? "error" : ""
                }`}
                type="email"
                name="verifierEmail"
                value={formData.verifierEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="verifier@company.com"
                disabled={loading}
              />
              {touched.verifierEmail && errors.verifierEmail && (
                <div className="edu-error">{errors.verifierEmail}</div>
              )}
              <small className="help-text">
                This email will receive a verification request when you click “Send/Resend Request” from the card.
              </small>
            </div>
          </div>

          <div className="edu-form-actions">
            <button
              type="button"
              className="edu-btn edu-btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="edu-btn edu-btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner" />
                  Saving...
                </>
              ) : employment ? (
                "Update Employment"
              ) : (
                "Save Employment"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmploymentForm;
